import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  MapPin,
  Plus,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  X,
} from 'lucide-react'
import { getFieldMarkers, createFieldMarker, getPaddocks } from '../../services/api'
import { format, parseISO } from 'date-fns'

const KIND_LABELS = {
  boundary: 'Boundary monument',
  paddock_corner: 'Paddock corner',
  monitoring_point_stake: 'Monitoring point stake',
  lantana_flag: 'Lantana flag',
  biochar_app_corner: 'Biochar application corner',
  kraal_post: 'Kraal post',
  water_point: 'Water point',
  other: 'Other',
}

const SURVEYED_LABELS = {
  dgps_handheld: 'DGPS handheld',
  survey_grade_gnss: 'Survey-grade GNSS',
  phone_gps: 'Phone GPS',
  tape_and_compass: 'Tape + compass',
  estimated: 'Estimated',
}

function MarkerForm({ ranchId, onClose }) {
  const queryClient = useQueryClient()
  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })
  const [form, setForm] = useState({
    kind: 'boundary',
    marker_tag: '',
    paddock_id: '',
    latitude: '',
    longitude: '',
    elevation_m: '',
    surveyed_with: 'phone_gps',
    horizontal_accuracy_m: '',
    notes: '',
    installed_at: format(new Date(), 'yyyy-MM-dd'),
  })

  const create = useMutation({
    mutationFn: (data) => createFieldMarker(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fieldMarkers', ranchId] })
      onClose()
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    create.mutate({
      ranch_id: ranchId,
      kind: form.kind,
      marker_tag: form.marker_tag,
      paddock_id: form.paddock_id || null,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      elevation_m: form.elevation_m ? parseFloat(form.elevation_m) : null,
      surveyed_with: form.surveyed_with,
      horizontal_accuracy_m: form.horizontal_accuracy_m ? parseFloat(form.horizontal_accuracy_m) : null,
      notes: form.notes || null,
      installed_at: form.installed_at || null,
    })
  }

  const captureGps = () => {
    if (!navigator.geolocation) {
      alert('Geolocation not supported in this browser')
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
          elevation_m: pos.coords.altitude ? pos.coords.altitude.toFixed(2) : f.elevation_m,
          horizontal_accuracy_m: pos.coords.accuracy ? pos.coords.accuracy.toFixed(2) : f.horizontal_accuracy_m,
        }))
      },
      (err) => alert(`GPS error: ${err.message}`),
      { enableHighAccuracy: true, timeout: 30000 },
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 mb-12">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold text-lg">New field marker</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
            <select
              value={form.kind}
              onChange={(e) => setForm(f => ({ ...f, kind: e.target.value }))}
              className="w-full border-gray-300 rounded-md shadow-sm"
            >
              {Object.entries(KIND_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Marker tag</label>
            <input
              type="text"
              required
              value={form.marker_tag}
              onChange={(e) => setForm(f => ({ ...f, marker_tag: e.target.value }))}
              placeholder="BND-001"
              className="w-full border-gray-300 rounded-md shadow-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Must be unique on this ranch.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Paddock (optional)</label>
            <select
              value={form.paddock_id}
              onChange={(e) => setForm(f => ({ ...f, paddock_id: e.target.value }))}
              className="w-full border-gray-300 rounded-md shadow-sm"
            >
              <option value="">— none / boundary —</option>
              {(paddocks || []).map(p => (
                <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <button type="button" onClick={captureGps} className="text-sm text-green-600 hover:text-green-700 hover:underline">
              Capture GPS now
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
              <input
                type="number"
                step="any"
                required
                value={form.latitude}
                onChange={(e) => setForm(f => ({ ...f, latitude: e.target.value }))}
                className="w-full border-gray-300 rounded-md shadow-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
              <input
                type="number"
                step="any"
                required
                value={form.longitude}
                onChange={(e) => setForm(f => ({ ...f, longitude: e.target.value }))}
                className="w-full border-gray-300 rounded-md shadow-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Surveyed with</label>
              <select
                value={form.surveyed_with}
                onChange={(e) => setForm(f => ({ ...f, surveyed_with: e.target.value }))}
                className="w-full border-gray-300 rounded-md shadow-sm"
              >
                {Object.entries(SURVEYED_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Accuracy (m)</label>
              <input
                type="number"
                step="0.1"
                value={form.horizontal_accuracy_m}
                onChange={(e) => setForm(f => ({ ...f, horizontal_accuracy_m: e.target.value }))}
                className="w-full border-gray-300 rounded-md shadow-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>

          {create.error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{create.error.response?.data?.detail || create.error.message}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700"
            >
              {create.isPending ? 'Saving...' : 'Create marker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function FieldMarkers({ ranchId }) {
  const [showForm, setShowForm] = useState(false)
  const [kindFilter, setKindFilter] = useState('')

  const { data: markers, isLoading } = useQuery({
    queryKey: ['fieldMarkers', ranchId, kindFilter],
    queryFn: () => getFieldMarkers(ranchId, kindFilter ? { kind: kindFilter } : {}),
    enabled: !!ranchId,
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MapPin className="h-6 w-6 text-green-600" />
              Field markers
            </h1>
            <p className="text-gray-500 mt-1">
              Surveyed physical markers on the ground. Anchor for boundary, paddock corners, and monitoring points.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New marker
        </button>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Filter by kind:</label>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="border-gray-300 rounded-md shadow-sm text-sm"
        >
          <option value="">All</option>
          {Object.entries(KIND_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-gray-500">Loading markers...</p>}

      {!isLoading && markers && markers.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <MapPin className="h-10 w-10 text-gray-400 mx-auto mb-2" />
          <h3 className="font-semibold text-gray-900">No field markers yet</h3>
          <p className="text-sm text-gray-500 mt-1">
            Drop a marker each time you install a boundary monument, paddock corner stake, or monitoring point.
          </p>
        </div>
      )}

      {!isLoading && markers && markers.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tag</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kind</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coordinates</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Surveyed</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Installed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {markers.map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-mono">{m.marker_tag}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{KIND_LABELS[m.kind] || m.kind}</td>
                  <td className="px-4 py-2 text-sm font-mono text-gray-600">
                    {m.latitude.toFixed(5)}, {m.longitude.toFixed(5)}
                    {m.horizontal_accuracy_m != null && (
                      <span className="text-xs text-gray-400 ml-2">±{m.horizontal_accuracy_m}m</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {SURVEYED_LABELS[m.surveyed_with] || m.surveyed_with || '—'}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {m.is_intact ? (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <CheckCircle2 className="h-4 w-4" /> intact
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-700">
                        <AlertCircle className="h-4 w-4" /> missing
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {m.installed_at ? format(parseISO(m.installed_at), 'd MMM yyyy') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <MarkerForm ranchId={ranchId} onClose={() => setShowForm(false)} />}
    </div>
  )
}
